import { useRef, useMemo } from 'react'
import { areArraysIdenticalByIds } from '@/utils/array.utils'

/**
 * Custom hook that returns a stable array reference when the items' IDs haven't changed.
 * This prevents unnecessary re-renders when arrays are recreated with identical data.
 * 
 * @param items The current items array (can change reference on every render)
 * @param getItemId Function to extract unique ID from each item
 * @returns A stable array reference that only changes when item IDs actually change
 * 
 * @example
 * const stableItems = useStableArrayByIds(orders, (order) => order.id)
 * // stableItems will maintain the same reference across renders if IDs are identical
 */
export function useStableArrayByIds<T>(
  items: T[] | undefined,
  getItemId: (item: T) => string
): T[] {
  const previousItemsRef = useRef<T[]>([])
  
  return useMemo(() => {
    const currentItems = items ?? []
    const prevItems = previousItemsRef.current
    
    // Compare arrays by item IDs - if identical, return previous reference to prevent re-render
    const itemsIdentical = areArraysIdenticalByIds(currentItems, prevItems, getItemId)
    
    if (itemsIdentical) {
      return prevItems
    }
    
    // Data changed, update ref and return new array
    previousItemsRef.current = currentItems
    return currentItems
  }, [items, getItemId])
}

